// src/api/notificationApi.js
import axios from "axios";
import notificationCache, {
  CACHE_KEYS,
  invalidateNotificationsCache,
} from "../utils/notification/notificationCache";
import { ERROR_TYPES } from "../utils/notification/errorHandler";
import { logError, ERROR_CATEGORIES } from "../utils/notification/errorLogger";

const apiUrl = process.env.REACT_APP_API_URL || "";

// Request deduplication cache
const pendingRequests = new Map();

// Create axios instance with default config
const notificationApiClient = axios.create({
  baseURL: apiUrl,
  timeout: 15000, // 15 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor for authentication
notificationApiClient.interceptors.request.use(
  (config) => {
    // Add request timestamp for performance monitoring
    config.metadata = { startTime: Date.now() };
    return config;
  },
  (error) => {
    logError(error, {
      category: ERROR_CATEGORIES.API,
      component: "NotificationAPI",
      action: "request_setup",
    });
    return Promise.reject(error);
  }
);

// Add response interceptor for comprehensive error handling
notificationApiClient.interceptors.response.use(
  (response) => {
    // Log successful requests in development
    if (process.env.NODE_ENV === "development") {
      const duration = Date.now() - response.config.metadata.startTime;
      console.log(
        `✅ API Success: ${response.config.method?.toUpperCase()} ${
          response.config.url
        } (${duration}ms)`
      );
    }
    return response;
  },
  (error) => {
    // Calculate request duration
    const duration = error.config?.metadata?.startTime
      ? Date.now() - error.config.metadata.startTime
      : null;

    // Enhance error with additional context
    if (error.code === "ECONNABORTED") {
      error.message = "Request timeout - please check your connection";
      error.type = ERROR_TYPES.TIMEOUT_ERROR;
    } else if (!error.response && error.request) {
      error.message = "Network error - please check your internet connection";
      error.type = ERROR_TYPES.NETWORK_ERROR;
    } else if (error.response?.status === 401) {
      error.type = ERROR_TYPES.AUTH_ERROR;
      error.message = "Authentication required - please log in";
    } else if (error.response?.status === 404) {
      error.type = ERROR_TYPES.NOT_FOUND_ERROR;
      error.message = "The requested resource was not found";
    } else if (error.response?.status === 429) {
      error.type = ERROR_TYPES.RATE_LIMIT_ERROR;
      error.message = "Too many requests - please wait before trying again";
      // Clear localStorage to prevent storage quota issues
      try {
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith("notification_error_")) {
            localStorage.removeItem(key);
          }
        });
      } catch (e) {
        console.warn("Failed to clear error logs:", e);
      }
    } else if (error.response?.status >= 400 && error.response?.status < 500) {
      error.type = ERROR_TYPES.CLIENT_ERROR;
      error.message = error.response?.data?.message || "Invalid request";
    } else if (error.response?.status >= 500) {
      error.type = ERROR_TYPES.SERVER_ERROR;
      error.message = "Server error - please try again later";
    } else {
      error.type = ERROR_TYPES.UNKNOWN_ERROR;
    }

    // Log error with context
    logError(error, {
      category: ERROR_CATEGORIES.API,
      component: "NotificationAPI",
      action: error.config?.url?.split("/").pop() || "unknown",
      metadata: {
        method: error.config?.method,
        url: error.config?.url,
        status: error.response?.status,
        duration,
      },
    });

    return Promise.reject(error);
  }
);

/**
 * Get paginated notifications for the current user
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (1-based)
 * @param {number} params.limit - Number of notifications per page
 * @param {string} params.type - Filter by notification type (optional)
 * @param {boolean} params.read - Filter by read status (optional)
 * @param {string} params.sort - Sort order (optional)
 * @param {string} token - Auth0 access token
 * @param {boolean} useCache - Whether to use cache (default: true)
 * @returns {Promise<Object>} Paginated notifications data
 */
export const getNotifications = async (params = {}, token, useCache = true) => {
  if (!token) {
    throw new Error("Authentication token is required");
  }

  const { page = 1, limit = 20, type, read, sort = "createdAt:desc" } = params;

  if (page < 1 || limit < 1 || limit > 100) {
    throw new Error("Invalid pagination parameters");
  }

  // Check cache first if enabled
  if (useCache) {
    const cacheKey = `${CACHE_KEYS.NOTIFICATIONS}_${JSON.stringify(params)}`;
    const cachedData = notificationCache.get(cacheKey);
    if (cachedData) {
      return cachedData;
    }
  }

  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    sort,
  });

  if (type) queryParams.append("type", type);
  if (typeof read === "boolean") queryParams.append("read", read.toString());

  const { data } = await notificationApiClient.get(
    `/api/notifications?${queryParams.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  // Cache the response if successful
  if (useCache && data) {
    const cacheKey = `${CACHE_KEYS.NOTIFICATIONS}_${JSON.stringify(params)}`;
    notificationCache.set(cacheKey, data);
  }

  return data;
};

/**
 * Get unread notification count for the current user
 * @param {string} token - Auth0 access token
 * @param {boolean} useCache - Whether to use cache (default: true)
 * @returns {Promise<Object>} Unread count data
 */
export const getUnreadCount = async (token, useCache = true) => {
  if (!token) {
    throw new Error("Authentication token is required");
  }

  // Check cache first if enabled
  if (useCache) {
    const cachedCount = notificationCache.get(CACHE_KEYS.UNREAD_COUNT);
    if (cachedCount !== null) {
      return { data: { unreadCount: cachedCount } };
    }
  }

  const { data } = await notificationApiClient.get(
    "/api/notifications/unread-count",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  // Cache the unread count if successful
  if (useCache && data && typeof data.unreadCount === "number") {
    notificationCache.set(CACHE_KEYS.UNREAD_COUNT, data.unreadCount);
  }

  return data;
};

/**
 * Mark a notification as read
 * @param {string} notificationId - The ID of the notification to mark as read
 * @param {string} token - Auth0 access token
 * @returns {Promise<Object>} Updated notification data
 */
export const markNotificationAsRead = async (notificationId, token) => {
  if (!notificationId || !token) {
    throw new Error("Notification ID and authentication token are required");
  }

  const { data } = await notificationApiClient.put(
    `/api/notifications/${notificationId}/read`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  // Invalidate cache after successful operation
  invalidateNotificationsCache();

  return data;
};

/**
 * Mark all notifications as read for the current user
 * @param {string} token - Auth0 access token
 * @returns {Promise<Object>} API response data
 */
export const markAllNotificationsAsRead = async (token) => {
  if (!token) {
    throw new Error("Authentication token is required");
  }

  const { data } = await notificationApiClient.put(
    "/api/notifications/mark-all-read",
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  // Invalidate cache after successful operation
  invalidateNotificationsCache();

  return data;
};

/**
 * Mark multiple notifications as read
 * @param {string[]} notificationIds - Array of notification IDs to mark as read
 * @param {string} token - Auth0 access token
 * @returns {Promise<Object>} API response data
 */
export const markMultipleNotificationsAsRead = async (
  notificationIds,
  token
) => {
  if (
    !notificationIds ||
    !Array.isArray(notificationIds) ||
    notificationIds.length === 0 ||
    !token
  ) {
    throw new Error(
      "Valid notification IDs array and authentication token are required"
    );
  }

  const { data } = await notificationApiClient.put(
    "/api/notifications/mark-multiple-read",
    { notificationIds },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  // Invalidate cache after successful operation
  invalidateNotificationsCache();

  return data;
};

/**
 * Delete a notification
 * @param {string} notificationId - The ID of the notification to delete
 * @param {string} token - Auth0 access token
 * @returns {Promise<Object>} API response data
 */
export const deleteNotification = async (notificationId, token) => {
  if (!notificationId || !token) {
    throw new Error("Notification ID and authentication token are required");
  }

  const { data } = await notificationApiClient.delete(
    `/api/notifications/${notificationId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  // Invalidate cache after successful operation
  invalidateNotificationsCache();

  return data;
};

/**
 * Delete multiple notifications
 * @param {string[]} notificationIds - Array of notification IDs to delete
 * @param {string} token - Auth0 access token
 * @returns {Promise<Object>} API response data
 */
export const deleteMultipleNotifications = async (notificationIds, token) => {
  if (
    !notificationIds ||
    !Array.isArray(notificationIds) ||
    notificationIds.length === 0 ||
    !token
  ) {
    throw new Error(
      "Valid notification IDs array and authentication token are required"
    );
  }

  const { data } = await notificationApiClient.delete(
    "/api/notifications/bulk-delete",
    {
      data: { notificationIds },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  // Invalidate cache after successful operation
  invalidateNotificationsCache();

  return data;
};

/**
 * Get notification preferences for the current user
 * @param {string} token - Auth0 access token
 * @returns {Promise<Object>} Notification preferences data
 */
export const getNotificationPreferences = async (token) => {
  if (!token) {
    throw new Error("Authentication token is required");
  }

  // Check for pending request to prevent duplicates
  const requestKey = `preferences_${token.substring(0, 10)}`;
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }

  // Create new request
  const requestPromise = notificationApiClient
    .get("/api/notifications/preferences", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((response) => {
      pendingRequests.delete(requestKey);
      return response.data;
    })
    .catch((error) => {
      pendingRequests.delete(requestKey);
      throw error;
    });

  // Store pending request
  pendingRequests.set(requestKey, requestPromise);

  return requestPromise;
};

/**
 * Update notification preferences for the current user
 * @param {Object} preferences - Notification preferences object
 * @param {boolean} preferences.follow - Whether to receive follow notifications
 * @param {string} token - Auth0 access token
 * @returns {Promise<Object>} Updated preferences data
 */
export const updateNotificationPreferences = async (preferences, token) => {
  if (!preferences || typeof preferences !== "object" || !token) {
    throw new Error(
      "Valid preferences object and authentication token are required"
    );
  }

  const { data } = await notificationApiClient.put(
    "/api/notifications/preferences",
    preferences,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};

/**
 * Error recovery strategies for notification API operations
 */
export const notificationErrorRecovery = {
  networkError: {
    retryAttempts: 3,
    retryDelay: [1000, 2000, 4000],
    fallbackAction: "showOfflineMessage",
  },
  authError: {
    action: "redirectToLogin",
    clearState: true,
  },
  notFoundError: {
    action: "removeFromState",
    showToast: true,
  },
  timeoutError: {
    retryAttempts: 2,
    retryDelay: [2000, 5000],
    fallbackAction: "showRetryButton",
  },
  serverError: {
    retryAttempts: 2,
    retryDelay: [3000, 6000],
    fallbackAction: "showErrorMessage",
  },
};

/**
 * Check if an error should be retried based on error type
 * @param {Error} error - The error object
 * @param {number} attemptCount - Current attempt count
 * @returns {boolean} Whether the error should be retried
 */
export const shouldRetryNotificationError = (error, attemptCount) => {
  const strategy = notificationErrorRecovery[error.type];
  if (!strategy || !strategy.retryAttempts) return false;
  return attemptCount < strategy.retryAttempts;
};

/**
 * Get retry delay for notification API errors
 * @param {Error} error - The error object
 * @param {number} attemptIndex - Current attempt index (0-based)
 * @returns {number} Delay in milliseconds
 */
export const getNotificationRetryDelay = (error, attemptIndex) => {
  const strategy = notificationErrorRecovery[error.type];
  if (!strategy || !strategy.retryDelay) return 1000;
  return (
    strategy.retryDelay[attemptIndex] ||
    strategy.retryDelay[strategy.retryDelay.length - 1]
  );
};
